import {
    cellAt,
    cellTitle,
    dayLabel,
    isWeekendDay,
    stripSummary,
} from '@apps/web/features/coverage/model/cell_model';
import {
    type CoverageRow,
    DAY_INDEXES,
    DAY_PARTS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { CELL_STATE_CLASSES } from '@apps/web/features/coverage/ui/availability_cell_styles';
import { cn } from '@apps/web/lib/utils';

/**
 * The 7x4 mini-grid for one caregiver: seven day columns, each a stack
 * of four day-part cells. One focus stop; the full labeled matrix lives
 * in the detail dialog the strip opens.
 */
export function AvailabilityStrip({
    row,
    workWeekStartDay,
    onOpenDetail,
}: {
    readonly row: CoverageRow;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly onOpenDetail: (row: CoverageRow) => void;
}) {
    return (
        <button
            type="button"
            className="hover:bg-muted flex items-end gap-1 rounded-md px-1 py-0.5"
            aria-label={stripSummary(row)}
            onClick={(event) => {
                event.stopPropagation();
                onOpenDetail(row);
            }}
        >
            {DAY_INDEXES.map((dayIndex) => {
                const weekend = isWeekendDay(dayIndex, workWeekStartDay);
                return (
                    <span
                        key={dayIndex}
                        className="flex flex-col items-center gap-0.5"
                        aria-hidden="true"
                    >
                        <span
                            className={cn(
                                'text-[9px] leading-none font-semibold',
                                weekend
                                    ? 'text-foreground'
                                    : 'text-muted-foreground',
                            )}
                        >
                            {dayLabel(dayIndex, workWeekStartDay).slice(0, 2)}
                        </span>
                        <span
                            className={cn(
                                'flex flex-col gap-px rounded-sm',
                                weekend &&
                                    'outline-border outline outline-offset-1',
                            )}
                        >
                            {DAY_PARTS.map((part) => (
                                <span
                                    key={part.key}
                                    title={cellTitle(
                                        row,
                                        dayIndex,
                                        part.key,
                                        workWeekStartDay,
                                    )}
                                    className={cn(
                                        'h-1.5 w-3.5 rounded-[2px]',
                                        CELL_STATE_CLASSES[
                                            cellAt(row, dayIndex, part.key)
                                                .state
                                        ],
                                    )}
                                />
                            ))}
                        </span>
                    </span>
                );
            })}
        </button>
    );
}
